<template>
  <!-- 카드 블록 — title + 라벨/값 필드(상태 pill·강조) + 액션(내부 nav / 외부 링크) -->
  <div class="ab-card">
    <div v-if="data.title" class="ab-card__title">{{ data.title }}</div>
    <dl v-if="fields.length" class="ab-card__list">
      <template v-for="(f, i) in fields" :key="i">
        <dt>{{ f.label }}</dt>
        <dd :class="{ 'ab-card__strong': f.strong }">
          <status-pill v-if="isPill(f.value)" :value="f.value" />
          <template v-else>{{ f.value }}</template>
        </dd>
      </template>
    </dl>
    <div v-if="actions.length" class="ab-card__actions">
      <template v-for="(a, i) in actions">
        <!-- 내부 네비 액션: navRegistry 허용목록 경유 router.push (OD-13) -->
        <button
          v-if="a.nav"
          :key="'n' + i"
          type="button"
          class="ab-card__action"
          :class="{ 'ab-card__action--outline': i > 0 }"
          @click="navigate(a.nav)"
        >📍 {{ a.label }}</button>
        <!-- 외부 링크 액션: http/https만, 새 탭 -->
        <a
          v-else
          :key="'h' + i"
          class="ab-card__action"
          :class="{ 'ab-card__action--outline': i > 0 }"
          :href="a.href"
          target="_blank"
          rel="noopener noreferrer"
        >{{ a.label }} ↗</a>
      </template>
    </div>
  </div>
</template>

<script>
import StatusPill from '../StatusPill.vue'
import { resolveStatusPill } from '../useStatusPill'
import { resolveNav } from '../navRegistry'
import { useAssistantNav } from '../useAssistantNav'

/**
 * @component CardBlock
 * @description data.title + data.fields([{label,value,strong?}]) + data.actions 카드.
 *   - 필드 값이 도메인 상태면 StatusPill, strong:true 면 강조(금액 등)
 *   - action.nav({entity,id,params})는 navRegistry 허용목록 통과 시에만 노출 → router.push
 *   - action.href 는 http/https 외부 링크만 (기존 스킴 제한 유지)
 */
export default {
  name: 'CardBlock',
  components: { StatusPill },
  props: {
    data: { type: Object, default: () => ({}) }
  },
  setup() {
    return { ...useAssistantNav() }
  },
  computed: {
    fields() { return Array.isArray(this.data.fields) ? this.data.fields : [] },
    actions() {
      return (Array.isArray(this.data.actions) ? this.data.actions : [])
        .filter((a) => {
          if (!a || !a.label) return false
          if (a.nav) return resolveNav(a.nav) != null // 허용목록 외/필수값 누락 액션은 숨김
          return /^https?:\/\//i.test(String(a.href || ''))
        })
    }
  },
  methods: {
    isPill(v) { return !!resolveStatusPill(v) }
  }
}
</script>

<style scoped>
.ab-card {
  border: 1px solid var(--color-primary-100, #C5D1E3);
  border-left: 3px solid var(--color-primary-500, #3B5998);
  border-radius: var(--radius-md, 8px);
  background: var(--color-white, #fff);
  padding: 12px;
}
.ab-card__title { font-weight: 700; font-size: 14px; color: var(--color-primary-500, #3B5998); margin-bottom: 8px; }
.ab-card__list { display: grid; grid-template-columns: auto 1fr; gap: 5px 12px; margin: 0; font-size: 13px; }
.ab-card__list dt { font-weight: 600; color: var(--color-gray-600, #5f6368); white-space: nowrap; }
.ab-card__list dd { margin: 0; color: var(--color-gray-800, #202124); word-break: break-word; }
/* 강조 필드(금액 등) — primary 톤 + 확대 */
.ab-card__strong { font-size: 15px; font-weight: 700; color: var(--color-primary-700, #1E3058); }
.ab-card__actions { display: flex; gap: 8px; margin-top: 10px; flex-wrap: wrap; }
.ab-card__action {
  font-size: 12.5px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: var(--radius-md, 8px);
  background: var(--color-primary-500, #3B5998);
  color: var(--color-white, #fff);
  text-decoration: none;
  border: 1px solid var(--color-primary-500, #3B5998);
  cursor: pointer;
  font-family: inherit;
  line-height: 1.4;
}
.ab-card__action:hover { background: var(--color-primary-600, #2C4578); }
/* 보조 액션(두 번째부터) — outline 톤 */
.ab-card__action--outline {
  background: var(--color-white, #fff);
  color: var(--color-primary-600, #2C4578);
}
.ab-card__action--outline:hover { background: var(--color-primary-50, #E8EDF4); }
</style>
